"use client";

import { ArrowRight, Anchor, Waves, Fish } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const collections = [{
  number: "COLLECTION 01",
  title: "The Deckhand",
  titleItalic: "Sampler Box",
  description: "A perfect introduction to our wild-caught bounty. Featuring a curated selection of our most popular fillets, flash-frozen at the peak of freshness to ensure premium quality from our boat to your kitchen.",
  meta: "4 varieties · 8 lbs total",
  catalog: "MY-DECK-001",
  wpQueryParams: {
    per_page: 4,
    product_cat: 16
  },
  ornament: "anchor" as const
}, {
  number: "COLLECTION 02",
  title: "The Captain's",
  titleItalic: "Reserve Box",
  description: "For the true seafood connoisseur. This premium collection includes our rarest cuts, including Black Cod and wild-caught Halibut, hand-selected by our crew for an unparalleled dining experience.",
  meta: "6 varieties · 12 lbs total",
  catalog: "MY-CAPT-002",
  wpQueryParams: {
    per_page: 4,
    product_cat: 17
  },
  ornament: "fish" as const
}, {
  number: "COLLECTION 03",
  title: "The Galley",
  titleItalic: "Pantry Essentials",
  description: "Ready-to-eat convenience without compromising on heritage quality. Our sustainably sourced tuna and salmon, preserved in premium olive oil, perfect for quick, nutritious meals at home.",
  meta: "8 tins · 4 varieties",
  catalog: "MY-GAL-003",
  wpQueryParams: {
    per_page: 4,
    product_cat: 16
  },
  ornament: "waves" as const
}];
function AlbumStack({
  reversed
}: {
  reversed: boolean;
}) {
  const {
    products,
    loading
  } = useEcommerceProducts();
  const rotations = [-2, 0, 2, 0];
  const placeholders = Array.from({
    length: 4
  });
  const items = (products && products.length > 0 ? products.slice(0, 4) : placeholders) as Array<{
    id?: number;
    title?: string;
    featured_image_url?: string;
  }>;
  return <div className="relative w-full flex items-center justify-center py-8 md:py-12">
      <div className="relative w-[260px] h-[260px] sm:w-[320px] sm:h-[320px] md:w-[380px] h-[380px] group">
        {items.map((item, i) => {
        const rotation = rotations[i] ?? 0;
        const offset = i * 8;
        return <div key={item?.id ?? i} data-index={i} className="absolute inset-0 transition-all duration-300 ease-out group-hover:[transform:var(--hover-transform)]" style={{
          transform: `translate(${reversed ? -offset : offset}px, ${i * 4}px) rotate(${rotation}deg)`,
          ["--hover-transform" as string]: `translate(${reversed ? -offset * 1.2 : offset * 1.2}px, ${i * 6}px) rotate(${rotation * 1.2}deg)`,
          zIndex: 10 + i
        } as React.CSSProperties}>
              <div className="w-full h-full overflow-hidden shadow-lg border border-border bg-card">
                {loading ? <div className="w-full h-full bg-muted animate-pulse" /> : item?.featured_image_url ? <img src={item.featured_image_url} alt={item.title ?? "Seafood product"} className="w-full h-full object-cover" loading="lazy" decoding="async" /> : <div className="w-full h-full flex items-center justify-center bg-muted">
                    <Fish className="w-16 h-16 text-primary/20" strokeWidth={1} />
                  </div>}
              </div>
            </div>;
      })}
      </div>
    </div>;
}
function Ornament({
  type
}: {
  type: "anchor" | "fish" | "waves";
}) {
  const common = "w-6 h-6 text-primary/60";
  if (type === "anchor") return <Anchor className={common} strokeWidth={1.5} />;
  if (type === "fish") return <Fish className={common} strokeWidth={1.5} />;
  return <Waves className={common} strokeWidth={1.5} />;
}
export default function Sampler() {
  return <section data-section-id="600" id="sampler-boxes" className="bg-background text-foreground relative overflow-hidden py-20 md:py-28 px-6 md:px-12">
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} />

      <div className="relative max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-end mb-20 md:mb-28">
          <div>
            <div className="font-mono text-xs tracking-widest uppercase text-primary mb-6">
              Heritage Selection / The Miss Yvonne
            </div>
            <h2 className="font-serif font-default text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight text-primary">
              Curated
              <br />
              <span className="italic font-light">Sampler Boxes.</span>
            </h2>
          </div>
          <div className="flex flex-col gap-6 lg:max-w-[360px] lg:justify-self-end">
            <p className="text-base md:text-lg text-foreground leading-relaxed">
              Built by hand and proven by time. Our sampler boxes bring the best of our Gulf Coast catch directly to your door, expertly cut and delivered with care.
            </p>
            <Link to="/ShopPage" className="inline-flex items-center gap-2 group w-fit">
              <span className="font-mono text-sm uppercase tracking-wider text-foreground border-b-2 border-primary pb-1 group-hover:text-primary transition-colors">
                Shop all boxes
              </span>
              <ArrowRight className="w-4 h-4 text-primary transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <div className="space-y-20 md:space-y-28">
          {collections.map((c, i) => {
          const reversed = i % 2 === 1;
          const wp_query = new WP_Query(c.wpQueryParams);
          return <div key={c.number} data-index={i}>
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center ${reversed ? "lg:[direction:rtl]" : ""}`}>
                  <div className={reversed ? "lg:[direction:ltr]" : ""}>
                    <EcommerceProductsProvider wp_query={wp_query}>
                      <AlbumStack reversed={reversed} />
                    </EcommerceProductsProvider>
                  </div>

                  <div className={`flex flex-col gap-5 ${reversed ? "lg:[direction:ltr]" : ""}`}>
                    <div className="font-mono text-xs tracking-widest uppercase text-primary">
                      {c.number}
                    </div>
                    <h3 className="font-serif font-default text-4xl md:text-5xl leading-[1.05] tracking-tight text-primary">
                      {c.title}{" "}
                      <span className="italic font-light">{c.titleItalic}</span>
                    </h3>
                    <p className="text-base md:text-lg text-foreground leading-relaxed max-w-xl">
                      {c.description}
                    </p>
                    <div className="font-mono text-xs uppercase tracking-wider text-foreground/70 flex flex-wrap items-center gap-3">
                      <span className="inline-block px-2 py-1 border border-border">
                        {c.meta.split("·")[0].trim()}
                      </span>
                      <span className="inline-block px-2 py-1 border border-border">
                        {c.meta.split("·")[1]?.trim()}
                      </span>
                    </div>
                    <div className="pt-3">
                      <Button asChild className="bg-secondary text-white hover:bg-primary px-10 py-6 h-auto font-mono text-xs uppercase tracking-widest shadow-md">
                        <Link to="/ShopPage" className="inline-flex items-center gap-2">
                          Order Now
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>

                {i < collections.length - 1 && <div className="relative mt-20 md:mt-28 flex items-center gap-4">
                    <Separator className="flex-1 bg-border" />
                    <div className="flex items-center gap-3 px-3 py-1.5 border border-border bg-background">
                      <Ornament type={c.ornament} />
                      <span className="font-mono text-[10px] uppercase tracking-widest text-primary">
                        {c.catalog}
                      </span>
                    </div>
                    <Separator className="flex-1 bg-border" />
                  </div>}
              </div>;
        })}
        </div>
      </div>
    </section>;
}